import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { RawEntry } from '@/hooks/useMonthlyReportData';
import { ReportFilters, DEFAULT_FILTERS } from '@/hooks/useSavedViews';
import { SavedViewsSelector } from './SavedViewsSelector';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface MonthlyReportTableProps {
  entries: RawEntry[];
  profilesList: Array<{ id: string; name: string }>;
  projectsList: Array<{ id: string; name: string }>;
}

type Grouping = 'none' | 'employee' | 'project' | 'week';

interface GroupedData {
  label: string;
  entries: RawEntry[];
  totalHours: number;
  totalAmount: number;
}

const formatHours = (minutes: number) => Math.round((minutes / 60) * 10) / 10;
const formatAmount = (minutes: number, rate: number | null) => Math.round(((minutes || 0) / 60) * (rate || 25));
const formatDate = (iso: string) => new Date(iso).toLocaleDateString('fr-CA');
const formatTime = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

const getWeekLabel = (iso: string) => {
  const d = new Date(iso);
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return `${monday.toLocaleDateString('fr-CA')} - ${sunday.toLocaleDateString('fr-CA')}`;
};

export const MonthlyReportTable: React.FC<MonthlyReportTableProps> = ({
  entries,
  profilesList,
  projectsList,
}) => {
  const [filters, setFilters] = useState<ReportFilters>(DEFAULT_FILTERS);
  const [grouping, setGrouping] = useState<Grouping>('none');
  const [showEmployeeFilter, setShowEmployeeFilter] = useState(false);
  const [showProjectFilter, setShowProjectFilter] = useState(false);

  // Filtrage
  const filtered = useMemo(() => {
    return entries.filter(e => {
      if (filters.employees.length > 0 && !filters.employees.includes(e.user_id)) return false;
      if (filters.projects.length > 0 && !filters.projects.includes(e.project_id)) return false;
      if (filters.status !== 'all' && e.status !== filters.status) return false;
      if (filters.billable === 'yes' && !e.is_billable) return false;
      if (filters.billable === 'no' && e.is_billable) return false;
      if (filters.invoiced === 'yes' && !e.is_invoiced) return false;
      if (filters.invoiced === 'no' && e.is_invoiced) return false;
      return true;
    });
  }, [entries, filters]);

  // Groupement
  const grouped = useMemo((): GroupedData[] => {
    if (grouping === 'none') {
      const totalMin = filtered.reduce((s, e) => s + (e.duration_minutes || 0), 0);
      const totalAmt = filtered.reduce((s, e) => s + formatAmount(e.duration_minutes || 0, e.hourly_rate), 0);
      return [{ label: '', entries: filtered, totalHours: formatHours(totalMin), totalAmount: totalAmt }];
    }

    const groups = new Map<string, RawEntry[]>();
    filtered.forEach(e => {
      let key: string;
      if (grouping === 'employee') key = e.employee_name;
      else if (grouping === 'project') key = e.project_name;
      else key = getWeekLabel(e.start_time);
      const arr = groups.get(key) || [];
      arr.push(e);
      groups.set(key, arr);
    });

    return Array.from(groups.entries()).map(([label, entries]) => {
      const totalMin = entries.reduce((s, e) => s + (e.duration_minutes || 0), 0);
      const totalAmt = entries.reduce((s, e) => s + formatAmount(e.duration_minutes || 0, e.hourly_rate), 0);
      return { label, entries, totalHours: formatHours(totalMin), totalAmount: totalAmt };
    }).sort((a, b) => b.totalHours - a.totalHours);
  }, [filtered, grouping]);

  const grandTotalMin = filtered.reduce((s, e) => s + (e.duration_minutes || 0), 0);
  const grandTotalAmt = filtered.reduce((s, e) => s + formatAmount(e.duration_minutes || 0, e.hourly_rate), 0);

  const toggleFilter = (arr: string[], id: string) =>
    arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id];

  const handleApplyView = (f: ReportFilters, g: string) => {
    setFilters(f);
    setGrouping(g as Grouping);
  };

  // Récapitulatif heures par projet
  const projectSummary = useMemo(() => {
    const map = new Map<string, { hours: number; employees: Set<string>; amount: number }>();
    filtered.forEach(e => {
      const curr = map.get(e.project_name) || { hours: 0, employees: new Set<string>(), amount: 0 };
      curr.hours += (e.duration_minutes || 0);
      curr.employees.add(e.user_id);
      curr.amount += formatAmount(e.duration_minutes || 0, e.hourly_rate);
      map.set(e.project_name, curr);
    });
    const totalH = formatHours(grandTotalMin);
    return Array.from(map.entries())
      .map(([name, d]) => ({
        name,
        hours: formatHours(d.hours),
        employees: d.employees.size,
        amount: d.amount,
        percentage: totalH > 0 ? Math.round((formatHours(d.hours) / totalH) * 100) : 0,
      }))
      .sort((a, b) => b.hours - a.hours);
  }, [filtered, grandTotalMin]);

  return (
    <div className="space-y-4">
      {/* Vues sauvegardées + Filtres */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <SavedViewsSelector currentFilters={filters} currentGrouping={grouping} onApplyView={handleApplyView} />

            {/* Groupement */}
            <div className="flex items-center gap-1 ml-auto">
              <span className="text-sm text-muted-foreground mr-1">Grouper:</span>
              {(['none', 'employee', 'project', 'week'] as Grouping[]).map(g => (
                <Button key={g} variant={grouping === g ? 'default' : 'outline'} size="sm" onClick={() => setGrouping(g)}>
                  {{ none: 'Aucun', employee: 'Employé', project: 'Projet', week: 'Semaine' }[g]}
                </Button>
              ))}
            </div>
          </div>

          {/* Ligne de filtres */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {/* Filtre employé */}
            <div className="relative">
              <Button variant="outline" size="sm" onClick={() => { setShowEmployeeFilter(!showEmployeeFilter); setShowProjectFilter(false); }}>
                Employé {filters.employees.length > 0 && `(${filters.employees.length})`} <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
              {showEmployeeFilter && (
                <Card className="absolute z-50 mt-1 w-56 shadow-lg max-h-60 overflow-y-auto">
                  <CardContent className="p-2">
                    {profilesList.map(p => (
                      <label key={p.id} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 rounded cursor-pointer">
                        <input type="checkbox" checked={filters.employees.includes(p.id)}
                          onChange={() => setFilters(f => ({ ...f, employees: toggleFilter(f.employees, p.id) }))} />
                        {p.name}
                      </label>
                    ))}
                    {filters.employees.length > 0 && (
                      <button className="text-xs text-blue-600 px-2 py-1" onClick={() => setFilters(f => ({ ...f, employees: [] }))}>
                        Tout effacer
                      </button>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Filtre projet */}
            <div className="relative">
              <Button variant="outline" size="sm" onClick={() => { setShowProjectFilter(!showProjectFilter); setShowEmployeeFilter(false); }}>
                Projet {filters.projects.length > 0 && `(${filters.projects.length})`} <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
              {showProjectFilter && (
                <Card className="absolute z-50 mt-1 w-56 shadow-lg max-h-60 overflow-y-auto">
                  <CardContent className="p-2">
                    {projectsList.map(p => (
                      <label key={p.id} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 rounded cursor-pointer">
                        <input type="checkbox" checked={filters.projects.includes(p.id)}
                          onChange={() => setFilters(f => ({ ...f, projects: toggleFilter(f.projects, p.id) }))} />
                        {p.name}
                      </label>
                    ))}
                    {filters.projects.length > 0 && (
                      <button className="text-xs text-blue-600 px-2 py-1" onClick={() => setFilters(f => ({ ...f, projects: [] }))}>
                        Tout effacer
                      </button>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Selects simples */}
            <select className="border rounded px-2 py-1" value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value as any }))}>
              <option value="all">Statut: Tous</option>
              <option value="active">Active</option>
              <option value="closed">Fermée</option>
            </select>

            <select className="border rounded px-2 py-1" value={filters.billable}
              onChange={e => setFilters(f => ({ ...f, billable: e.target.value as any }))}>
              <option value="all">Facturable: Tous</option>
              <option value="yes">Oui</option>
              <option value="no">Non</option>
            </select>

            <select className="border rounded px-2 py-1" value={filters.invoiced}
              onChange={e => setFilters(f => ({ ...f, invoiced: e.target.value as any }))}>
              <option value="all">Facturé: Tous</option>
              <option value="yes">Oui</option>
              <option value="no">Non</option>
            </select>

            {(filters.employees.length > 0 || filters.projects.length > 0 || filters.status !== 'all' || filters.billable !== 'all' || filters.invoiced !== 'all') && (
              <button className="text-xs text-red-600 underline" onClick={() => setFilters(DEFAULT_FILTERS)}>
                Réinitialiser
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tableau principal */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Employé</th>
                  <th className="text-left px-3 py-2">Projet</th>
                  <th className="text-right px-3 py-2">Heures</th>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-center px-3 py-2">Statut</th>
                  <th className="text-center px-3 py-2">Fact.</th>
                  <th className="text-center px-3 py-2">Facturé</th>
                  <th className="text-right px-3 py-2">Taux</th>
                  <th className="text-right px-3 py-2">Montant</th>
                </tr>
              </thead>
              <tbody>
                {grouped.map((group, gi) => (
                  <React.Fragment key={gi}>
                    {group.label && (
                      <tr className="bg-blue-50 border-t">
                        <td colSpan={10} className="px-3 py-2 font-semibold text-blue-800">{group.label}</td>
                      </tr>
                    )}
                    {group.entries.map((e, ei) => (
                      <tr key={ei} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-1.5">{formatDate(e.start_time)}</td>
                        <td className="px-3 py-1.5">{e.employee_name}</td>
                        <td className="px-3 py-1.5">{e.project_name}</td>
                        <td className="px-3 py-1.5 text-right">{formatHours(e.duration_minutes || 0)}h</td>
                        <td className="px-3 py-1.5 max-w-[200px] truncate" title={e.description || ''}>{e.description || '-'}</td>
                        <td className="px-3 py-1.5 text-center">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${e.status === 'closed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {e.status === 'closed' ? 'Fermée' : 'Active'}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-center">{e.is_billable ? 'Oui' : 'Non'}</td>
                        <td className="px-3 py-1.5 text-center">{e.is_invoiced ? 'Oui' : 'Non'}</td>
                        <td className="px-3 py-1.5 text-right">{e.hourly_rate ? `${e.hourly_rate}$` : '-'}</td>
                        <td className="px-3 py-1.5 text-right">{formatAmount(e.duration_minutes || 0, e.hourly_rate)}$</td>
                      </tr>
                    ))}
                    {group.label && (
                      <tr className="bg-gray-100 font-semibold">
                        <td colSpan={3} className="px-3 py-1.5 text-right">Sous-total {group.label}</td>
                        <td className="px-3 py-1.5 text-right">{group.totalHours}h</td>
                        <td colSpan={5}></td>
                        <td className="px-3 py-1.5 text-right">{group.totalAmount}$</td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {/* Grand total */}
                <tr className="bg-gray-200 font-bold border-t-2">
                  <td colSpan={3} className="px-3 py-2 text-right">TOTAL</td>
                  <td className="px-3 py-2 text-right">{formatHours(grandTotalMin)}h</td>
                  <td colSpan={5}></td>
                  <td className="px-3 py-2 text-right">{grandTotalAmt}$</td>
                </tr>
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <p className="text-center text-muted-foreground py-8">Aucune entrée pour cette période avec ces filtres</p>
          )}
        </CardContent>
      </Card>

      {/* Récapitulatif heures par projet */}
      <Card>
        <CardHeader><CardTitle>Heures par projet</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left px-3 py-2">Projet</th>
                <th className="text-right px-3 py-2">Heures</th>
                <th className="text-right px-3 py-2">Employés</th>
                <th className="text-right px-3 py-2">Montant</th>
                <th className="text-right px-3 py-2">%</th>
              </tr>
            </thead>
            <tbody>
              {projectSummary.map((p, i) => (
                <tr key={i} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-1.5 font-medium">{p.name}</td>
                  <td className="px-3 py-1.5 text-right">{p.hours}h</td>
                  <td className="px-3 py-1.5 text-right">{p.employees}</td>
                  <td className="px-3 py-1.5 text-right">{p.amount}$</td>
                  <td className="px-3 py-1.5 text-right">{p.percentage}%</td>
                </tr>
              ))}
              <tr className="bg-gray-200 font-bold">
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right">{formatHours(grandTotalMin)}h</td>
                <td className="px-3 py-2 text-right">{new Set(filtered.map(e => e.user_id)).size}</td>
                <td className="px-3 py-2 text-right">{grandTotalAmt}$</td>
                <td className="px-3 py-2 text-right">100%</td>
              </tr>
            </tbody>
          </table>
          {projectSummary.length === 0 && (
            <p className="text-center text-muted-foreground py-8">Aucune donnée</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
